import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Breadcrumbs, Link as MuiLink, Typography } from '@mui/material';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { bindActionCreators } from 'redux';
import CountryMenu from '/@/components/country-menu';
import Link from '/@/components/link';
import LocalePreferences from '/@/components/locale-preferences';
import IconNotification from '/@/components/notification/icon-notification';
import ReceiveNotificationMessages from '/@/components/receive-notification-messages';
import settings, { isExpertoBayer } from '/@/core/settings';
import { _ } from '/@/locale';
import NestedMenu from '/@/mui/nested-menu';
import { checkInternal, getMenuItems } from '/@/settings/menu';
import { configureViews } from '/@/settings/views';
import { getAskAnExpertEnabled } from '/@/store/ask-an-expert/selectors';
import { getBagQualityEnabled } from '/@/store/bag-quality/selectors';
import { getCarbonCalculatorBREnabled, getCarbonCalculatorEnabled } from '/@/store/carbon-calculator/selectors';
import { useCatalogZones } from '/@/store/catalog/apiProviders';
import { getCatalogEnabled } from '/@/store/catalog/selectors';
import { getFieldTrialsNetworkEnabled } from '/@/store/field-trial-network/selectors';
import { getLibraryEnabled } from '/@/store/library/selectors';
import { getSoyMultipliersEnabled } from '/@/store/multipliers/selectors';
import { openMenu, setSelectedFeature, setShowTermsAndConditions } from '/@/store/nav/actions';
import { getGoBack } from '/@/store/nav/selectors';
import { getWeedsRecommendationEnabled } from '/@/store/private-data/recommendation-weeds/selectors';
import { logout, setPreferences } from '/@/store/session/actions';
import {
    getAppInstance,
    getCornRecommendationEnabled,
    getMapsEnabled,
    getNitrogenRecommendationEnabled,
    getSessionBrands,
    getSessionCountries,
    getSessionCountry,
    getSessionLanguage,
    getShowReceiveNotificationMessages,
    getSoyRecommendationEnabled,
    getUser,
} from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking/actions';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { getWeatherFeatureEnabled } from '/@/store/weather/selectors';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { reverse } from '/@/utils/router';
import styles from './styles.module.css';

/** @param {typeof propTypes} props */
function AppHeader(props) {
    const offline = useSelector((state) => state.network.offline);
    const country = useSelector(getSessionCountry);
    const countries = useSelector(getSessionCountries);
    const showReceiveNotificationMessages = useSelector(getShowReceiveNotificationMessages);
    const user = useSelector(getUser);
    const language = useSelector(getSessionLanguage);
    const goBack = useSelector(getGoBack);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const actions = useMemo(
        () =>
            bindActionCreators(
                { openMenu, setPreferences, logout, setSelectedFeature, setShowTermsAndConditions, trackEvent },
                dispatch
            ),
        [dispatch]
    );

    const catalogZonesQuery = useCatalogZones({ page: 1, pageSize: 10000, type: 'catalog' });
    const catalogZones = useMemo(() => catalogZonesQuery.data?.results || [], [catalogZonesQuery.data?.results]);

    const sessionBrands = useSelector(getSessionBrands);
    const appInstance = useSelector(getAppInstance);
    const libraryEnabled = useSelector(getLibraryEnabled);
    const catalogEnabled = useSelector((state) => getCatalogEnabled(state, catalogZones));
    const nitrogenRecommendationEnabled = useSelector(getNitrogenRecommendationEnabled);
    const fieldTrialNetworkEnabled = useSelector(getFieldTrialsNetworkEnabled);
    const enableAskAnExpert = useSelector(getAskAnExpertEnabled);
    const bagQualityEnabled = useSelector(getBagQualityEnabled);
    const soyMultipliersEnabled = useSelector(getSoyMultipliersEnabled);
    const mapsEnabled = useSelector(getMapsEnabled);
    const carbonCalculatorEnabled = useSelector(getCarbonCalculatorEnabled);
    const carbonCalculatorBREnabled = useSelector(getCarbonCalculatorBREnabled);
    const soyRecommendationEnabled = useSelector((state) => getSoyRecommendationEnabled(state, appInstance));
    const cornRecommendationEnabled = useSelector((state) => getCornRecommendationEnabled(state, appInstance));
    const weatherEnabled = useSelector(getWeatherFeatureEnabled);
    const weedsEnabled = useSelector(getWeedsRecommendationEnabled);

    const menuItems = getMenuItems({
        user: user,
        brands: sessionBrands,
        appInstance: appInstance,
        sessionFlags: {
            enableCornHeadToHead: appInstance.enableCornHeadToHead && checkInternal(user, appInstance),
            enableSoyHeadToHead: appInstance.enableSoyHeadToHead && checkInternal(user, appInstance),
            libraryEnabled: libraryEnabled,
            catalogEnabled: catalogEnabled,
            enableWeedsFinder: appInstance.enableWeedFinder,
            nitrogenRecommendationEnabled: nitrogenRecommendationEnabled,
            fieldTrialNetworkEnabled: fieldTrialNetworkEnabled,
            enableAskAnExpert: enableAskAnExpert,
            bagQualityEnabled: bagQualityEnabled,
            soyMultipliersEnabled: soyMultipliersEnabled,
            mapsEnabled: mapsEnabled,
            carbonCalculatorEnabled: carbonCalculatorEnabled,
            carbonCalculatorBREnabled: carbonCalculatorBREnabled,
            soyRecommendationEnabled: soyRecommendationEnabled,
            cornRecommendationEnabled: cornRecommendationEnabled,
            productDatasheetEnabled: appInstance.enableProductDatasheet && checkInternal(user, appInstance),
            weatherEnabled: weatherEnabled,
            weedsEnabled: weedsEnabled,
            cropMonitoringEnabled: appInstance.enableCropMonitoring,
            frostRiskEnabled: appInstance.enableFrostRisk,
        },
    });

    function handleCountryChange(country) {
        if (user) {
            navigate(reverse('NewHome'));
        }
        actions.setPreferences({ country: country.id });
    }

    function handleChange(data) {
        actions.setPreferences(data);
    }

    const rootClassNames = classNames(styles.root, {
        [styles.border]: props.noShadow,
        [styles.shadow]: !props.noShadow,
        [styles.showReceiveNotificationMessages]: showReceiveNotificationMessages,
    });

    const navigateGoBack = () => navigate(-1);

    const navigateUserPreferences = () => navigate(reverse('UserPreferences'));

    const navigateUserProfile = () => {
        mpTrackEvent('Access to My Profile', undefined);
        navigate(reverse('Profile'));
    };

    const handleLogout = async () => {
        actions.logout(true);
        navigate(reverse('Landing'));
    };

    const handleMenuItemClick = (view, feature, crop, qs = null, params = null, trackMethod) => {
        if (!view) return null;
        if (trackMethod) trackMethod();
        actions.setSelectedFeature({ view: view, selectedFeature: feature, crop: crop || null });
        const trackingKey = crop ? `${view}.${feature}.${crop}` : `${view}.${feature}`;
        actions.trackEvent({ key: trackingKey.toLowerCase() });
        const url = reverse(view, { qs, params });
        navigate(url);
    };

    const renderMenuItem = (menuItem) => {
        if (menuItem.children) {
            menuItem.children = menuItem.children.map((children) => {
                return renderMenuItem(children);
            });
        }
        return {
            label: menuItem.label,
            disabled: offline && menuItem.disableOffline,
            children: menuItem.children,
            onMenuItemClick: menuItem.onClick
                ? () =>
                      menuItem.onClick(
                          // fallback default function for menu item
                          (view, selectedFeature, crop, qs, params, trackMethod) =>
                              handleMenuItemClick(view, selectedFeature, crop, qs, params, trackMethod),
                          // extra data for specific menu items, to be used in ui/src/settings/menu.ts
                          {
                              country: country,
                              tcFallback: () => actions.setShowTermsAndConditions(true),
                          }
                      )
                : () =>
                      // if menu item config doesn't have on click function
                      handleMenuItemClick(
                          menuItem.view,
                          menuItem.selectedFeature,
                          menuItem.crop,
                          menuItem.qs,
                          menuItem.params,
                          menuItem.trackMethod
                      ),
        };
    };

    return (
        <div className={rootClassNames}>
            {showReceiveNotificationMessages && <ReceiveNotificationMessages />}
            <div className={styles.content}>
                <Link to='Login' className={styles.appName}>
                    {isExpertoBayer() ? (
                        <div
                            className={classNames(styles.favicon, styles[`theme-${settings.app.theme}`])}
                            style={{
                                backgroundImage: `url('${settings.app.logo}')`,
                            }}
                        />
                    ) : (
                        <>
                            <div
                                className={classNames(styles.favicon, styles[`theme-${settings.app.theme}`])}
                                style={{ backgroundImage: `url('${settings.app.favicon}')` }}
                            />
                            <span className={styles.text}>{settings.app.name}</span>
                        </>
                    )}
                </Link>
                <Box flexGrow={1} />
                <div className={styles.right}>
                    {user && <IconNotification />}
                    <div className={styles.countryNoUser}>
                        {!user && (
                            <LocalePreferences
                                openUp
                                appearance='row'
                                showLabels={false}
                                country={country.id}
                                language={language}
                                onChange={handleChange}
                                showCountry
                                whiteColor
                                textColor='grey'
                                handleCountryChange={handleChange}
                                upperCase
                            />
                        )}
                        {user && !offline && (
                            <CountryMenu
                                openUp
                                countries={countries}
                                selectedCountry={country}
                                onChange={handleCountryChange}
                            />
                        )}
                    </div>
                    {user && (
                        <NestedMenu
                            Icon={PersonIcon}
                            tooltipTitle={_('Preferencias de usuario')}
                            menuItems={[
                                { label: _('Mi Perfil'), ItemIcon: PersonIcon, onMenuItemClick: navigateUserProfile },
                                {
                                    label: _('Ajustes'),
                                    ItemIcon: SettingsIcon,
                                    onMenuItemClick: navigateUserPreferences,
                                },
                                { label: _('Cerrar sesión'), ItemIcon: LogoutIcon, onMenuItemClick: handleLogout },
                            ]}
                        />
                    )}
                    {/* Menu item config in ui/src/settings/menu.ts */}
                    {user && (
                        <NestedMenu
                            tooltipTitle={_('Menú')}
                            Icon={MenuIcon}
                            menuItems={menuItems.map((item) => renderMenuItem(item))}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

const propTypes = {
    onClickBurgerMenu: PropTypes.func,
    noShadow: PropTypes.bool,
    login: PropTypes.bool,
    hideBackButton: PropTypes.bool,
};
AppHeader.propTypes = propTypes;
export default AppHeader;

const AppHeaderBreadcrumb = () => {
    const isMobile = useIsMobile();

    const currentView = useSelector((state) => {
        const { viewsByName } = configureViews();
        return state.nav.activeView ? viewsByName[state.nav.activeView.name] : undefined;
    });

    const params = useParams();
    if (isMobile || !currentView || !currentView.getBreadcrumbProps) return null;
    return (
        <Breadcrumbs>
            <MuiLink underline='hover' sx={{ cursor: 'pointer' }}>
                <Link to='NewHome'>{_('Home')}</Link>
            </MuiLink>
            {currentView.getBreadcrumbProps({ params }).map(({ to, label, params = {} }, index, arr) => {
                const parsedLabel = typeof label === 'function' ? label() : label;
                return (
                    (index !== arr.length - 1 && (
                        <MuiLink underline='hover' sx={{ cursor: 'pointer' }}>
                            <Link key={to} to={to} params={params}>
                                {parsedLabel}
                            </Link>
                        </MuiLink>
                    )) || <Typography>{parsedLabel}</Typography>
                );
            })}
        </Breadcrumbs>
    );
};
